import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Spinner, Flex } from "@chakra-ui/react";
import PlayerCard from "../../../../shared/players/PlayerCard";
import { useTheme } from "../../../../shared/common/ThemeContext";
import { useSimulation } from "../../../../shared/players/SimulationContext";
import UploadModal from "../../../../shared/simulation/UploadModal";
import { usePlayersPerf } from "../../../../shared/common/PlayersPerfContext";
import { encodeId } from "../../../../shared/common/hashid.js";
import { STORAGE_PREFIX } from "@config";
import { useTranslation } from "react-i18next";
import { hasLoadSimulationData } from "../../../../shared/common/playerRiskUtils.js";
import {
  PLAYER_POSITIONS,
  PLAYER_POSITION_ORDER,
  normalizePlayerPosition,
} from "../../../../shared/common/PlayerPositions";

const PlayersTable = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { simulations, addSimulation } = useSimulation();

  // ⬇️ on récupère maintenant aussi l’erreur + éventuellement le flag fallback
  const {
    players: rawPlayers,
    loading,
    error,
    isFallback,        // si tu l’as ajouté dans le context, sinon ce sera undefined
  } = usePlayersPerf();

  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const club_id = localStorage.getItem(`${STORAGE_PREFIX}club_id`) || localStorage.getItem(`${STORAGE_PREFIX}user_id`);

  // 🛡️ Toujours travailler avec un tableau sûr
  const safePlayers = useMemo(
    () => (Array.isArray(rawPlayers) ? rawPlayers : []),
    [rawPlayers]
  );

  const simulationPlayers = useMemo(
    () => safePlayers.filter(hasLoadSimulationData),
    [safePlayers]
  );

  const filteredPlayers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return simulationPlayers;

    return simulationPlayers.filter(
      (player) =>
        (player.first_name || "").toLowerCase().includes(term) ||
        (player.last_name || "").toLowerCase().includes(term)
    );
  }, [simulationPlayers, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCardClick = (id) => navigate(`/players/${encodeId(id)}`);

  const groupedPlayers = PLAYER_POSITION_ORDER.reduce((acc, position) => {
    acc[position] = [];
    return acc;
  }, {});

  (filteredPlayers || []).forEach((player) => {
    const normalizedPosition = normalizePlayerPosition(player.position);

    if (!groupedPlayers[normalizedPosition]) {
      groupedPlayers[normalizedPosition] = [];
    }

    groupedPlayers[normalizedPosition].push({
      ...player,
      position: normalizedPosition,
    });
  });

  const hasAnyPlayer = Object.values(groupedPlayers).some(
    (arr) => arr.length > 0
  );

  return (
    <>
      <motion.div
        className={`bg-${isDarkMode ? "gray-800" : "white"} bg-opacity-50 backdrop-blur-md shadow-lg rounded-xl p-6 border ${
          isDarkMode ? "border-gray-700" : "border-gray-300"
        }`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {/* 🔔 Bandeau d’info si on est en mode fallback ou si l’API a renvoyé une erreur */}
        {(error || isFallback) && (
          <div
            className={`mb-4 rounded-xl border px-3 py-2 text-xs sm:text-sm ${
              isDarkMode
                ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                : "border-amber-400 bg-amber-50 text-amber-800"
            }`}
          >
            {t("pages.players.load_error")}
            {" "}
            <span className="font-semibold">
              {t("pages.players.load_error_detail")}
            </span>
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="p-0 sm:p-2">
            <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center space-x-2 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
                >
                  <Plus size={20} color="white" />
                  <span>{t("pages.players.new_simulation")}</span>
                </button>
            </div>
          </div>
            <div className="flex items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("pages.players.search")}
                  className={`bg-${
                    isDarkMode ? "gray-700" : "white"
                  } text-${isDarkMode ? "white" : "black"} placeholder-${
                    isDarkMode ? "gray-400" : "gray-600"
                  } rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <Search
                  className={`absolute left-3 top-2.5 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                  size={18}
                />
              </div>
            </div>
        </div>

        {loading ? (
          <Flex justifyContent={"center"}>
            <Spinner size={"xl"} />
          </Flex>
        ) : !hasAnyPlayer ? (
          <div
            className={`text-center py-10 text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {t("pages.players.empty")}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedPlayers).map(([position, playersByPos]) =>
              playersByPos.length ? (
                <div key={position}>
                  <h3
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-gray-100" : "text-black"
                    }`}
                  >
                    {t(
                      PLAYER_POSITIONS.find((p) => p.value === position)?.labelKey ||
                        `positions.${position.toLowerCase()}`
                    )}
                  </h3>

                  <div className="flex flex-wrap justify-self-start gap-8">
                    {playersByPos.map((player) => (
                      <motion.div
                        key={player.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <PlayerCard
                          player={player}
                          isSimulating={
                            simulations &&
                            Object.values(simulations).some(
                              (s) =>
                                s.player_id === player.id &&
                                (s.status === "in_progress" || s.status === "queued")
                            )
                          }
                          onFileDrop={(file) => addSimulation(player.id, file, "field1")}
                          onCardClick={() => handleCardClick(player.id)}
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : null
            )}
          </div>
        )}
      </motion.div>

      {/* Modal upload simulation */}
      <motion.div
        className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 ${
          isModalOpen ? "flex" : "hidden"
        }`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <UploadModal
          club_id={club_id}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          addSimulation={addSimulation}
        />
      </motion.div>

      {/* ChatDock consomme aussi les joueurs du contexte */}
      {/* <ChatDock
        players={safePlayers}
        clubId={club_id}
        isDarkMode={isDarkMode}
      /> */}
    </>
  );
};

export default PlayersTable;
